<script setup>
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import SiteImageField from '@/components/Sites/SiteImageField.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import { LINKS_MAX, LINK_LABEL_MAX } from '@/components/Sites/broadcastAudience'

// Settings › Broadcast › Sponsor & links (site-admin API v0.19): the sponsor
// logo in the header, up to five links under the player and a backup stream
// URL. Each block shows only when the runtime lists its key; the page owns
// the form, the validation and the PATCH.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	/** `{ broadcastSponsorLogo, broadcastLinks, broadcastBackupStreamUrl, links: string[] }` messages. */
	errors: {
		type: Object,
		default: () => ({}),
	},
	writableKeys: {
		type: Array,
		default: null,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	/** Current sponsor label (Theme › Texts override or the shipped “Stream supported by”). */
	sponsorLabel: {
		type: String,
		default: 'Stream supported by',
	},
})
const form = defineModel({ type: Object, required: true })

const writable = (key) => !Array.isArray(props.writableKeys) || props.writableKeys.includes(key)
const set = (key, value) => { form.value = { ...form.value, [key]: value } }
const links = () => form.value.broadcastLinks || []
const updateLink = (i, patch) => set('broadcastLinks', links().map((l, idx) => (idx === i ? { ...l, ...patch } : l)))
const removeLink = (i) => set('broadcastLinks', links().filter((_, idx) => idx !== i))
const addLink = () => { if (links().length < LINKS_MAX) set('broadcastLinks', [...links(), { label: '', url: '' }]) }
</script>

<template>
	<section v-if="writable('broadcastSponsorLogo') || writable('broadcastLinks') || writable('broadcastBackupStreamUrl')" class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="broadcast-event-extras">
		<h3 class="mb-1 text-sm font-semibold text-white">Sponsor &amp; links</h3>
		<p class="mb-4 text-tiny text-surface-7">A sponsor logo in the header, quick links under the player (agenda, FAQ, speakers…) and a backup stream for viewers if the main one has trouble.</p>

		<div class="flex flex-col gap-4">
			<div v-if="writable('broadcastSponsorLogo')" class="min-w-0" data-testid="broadcast-sponsor-field">
				<SiteImageField id="broadcast-sponsor-logo" :model-value="form.broadcastSponsorLogo" :site-id="siteId" label="Sponsor logo" preview="logo"
					:hint="`Shown in the header after “${sponsorLabel}”. PNG or WebP with a transparent background, about 320×80 px. Change the label under Theme › Texts.`"
					@update:model-value="set('broadcastSponsorLogo', $event)" />
				<p v-if="errors.broadcastSponsorLogo" class="mb-0 mt-1.5 text-tiny text-error">{{ errors.broadcastSponsorLogo }}</p>
			</div>

			<div v-if="writable('broadcastLinks')" class="min-w-0" data-testid="broadcast-links-editor">
				<p class="mb-1 text-tiny font-medium text-surface-text-1">Links under the player</p>
				<div class="flex flex-col gap-2">
					<div v-for="(link, i) in form.broadcastLinks" :key="i" class="min-w-0" :data-testid="`broadcast-link-row-${i}`">
						<div class="extras-field flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
							<FormInput :id="`broadcast-link-label-${i}`" :model-value="link.label" label="Label" placeholder="e.g. Agenda" input-classes="placeholder:text-surface-6" :maxlength="LINK_LABEL_MAX" :disabled="disabled" class="min-w-0 sm:w-40 sm:shrink-0" @update:model-value="updateLink(i, { label: $event })" />
							<FormInput :id="`broadcast-link-url-${i}`" :model-value="link.url" label="URL" placeholder="https://… or /agenda" input-classes="placeholder:text-surface-6" :disabled="disabled" :is-error="Boolean(errors.links?.[i])" class="min-w-0 flex-1" @update:model-value="updateLink(i, { url: $event })" />
							<FormButton is-icon type="danger" size="md" title="Remove link" :aria-label="`Remove link ${i + 1}`" class="mb-px self-end" :disabled="disabled" @click="removeLink(i)">
								<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
							</FormButton>
						</div>
						<p v-if="errors.links?.[i]" class="mb-0 mt-1 text-tiny text-error" :data-testid="`broadcast-link-error-${i}`">{{ errors.links[i] }}</p>
					</div>
					<div class="flex flex-wrap items-center gap-3">
						<FormButton type="secondary" size="sm" :disabled="disabled || (form.broadcastLinks || []).length >= LINKS_MAX" data-testid="broadcast-link-add" @click="addLink">
							<icon-base class="mr-1" :width="12" :height="12"><icon-plus /></icon-base>
							Add link
						</FormButton>
						<span class="text-tiny text-surface-7">Up to {{ LINKS_MAX }}. Links to other websites open in a new tab.</span>
					</div>
				</div>
			</div>

			<div v-if="writable('broadcastBackupStreamUrl')" class="extras-field min-w-0" data-testid="broadcast-backup-field">
				<FormInput id="broadcast-backup-url" :model-value="form.broadcastBackupStreamUrl" label="Backup stream URL" placeholder="https://…" input-classes="placeholder:text-surface-6" :disabled="disabled" :is-error="Boolean(errors.broadcastBackupStreamUrl)" @update:model-value="set('broadcastBackupStreamUrl', $event)" />
				<p class="mb-0 mt-1.5 text-tiny" :class="errors.broadcastBackupStreamUrl ? 'text-error' : 'text-surface-7'">
					{{ errors.broadcastBackupStreamUrl || 'Optional. Adds a “Backup stream” link under the player while you are live — for example a YouTube or a second Castr stream page. Only viewers who can watch the main stream see it.' }}
				</p>
			</div>
		</div>
	</section>
</template>
